import React, { useState } from 'react';
import {
  Button,
  Dropdown,
  Option,
  Input,
  Checkbox,
  makeStyles,
  shorthands,
} from '@fluentui/react-components';
import { BaseDialog } from './BaseDialog';

const useStyles = makeStyles({
  field: {
    marginBottom: '16px',
  },
  fieldRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '16px',
  },
  fieldLabel: {
    display: 'flex',
    alignItems: 'center',
    minWidth: '150px',
  },
  fieldInput: {
    flex: 1,
  },
});

interface BulkEditCustomersModalProps {
  isOpen: boolean;
  onClose: () => void;
  customerCount: number;
  onSave: (data: BulkEditData) => void;
}

export interface BulkEditData {
  securityDeposit?: {
    enabled: boolean;
    value: string;
  };
  taxProfile?: {
    enabled: boolean;
    value: string;
  };
  discountPercentage?: {
    enabled: boolean;
    value: string;
  };
}

export default function BulkEditCustomersModal({
  isOpen,
  onClose,
  customerCount,
  onSave,
}: BulkEditCustomersModalProps) {
  const styles = useStyles();
  const [securityDepositEnabled, setSecurityDepositEnabled] = useState(false);
  const [securityDepositValue, setSecurityDepositValue] = useState('');
  const [taxProfileEnabled, setTaxProfileEnabled] = useState(false);
  const [taxProfileValue, setTaxProfileValue] = useState('');
  const [discountEnabled, setDiscountEnabled] = useState(false);
  const [discountValue, setDiscountValue] = useState('');

  const handleSave = () => {
    const data: BulkEditData = {};

    if (securityDepositEnabled) {
      data.securityDeposit = {
        enabled: true,
        value: securityDepositValue,
      };
    }

    if (taxProfileEnabled) {
      data.taxProfile = {
        enabled: true,
        value: taxProfileValue,
      };
    }

    if (discountEnabled) {
      data.discountPercentage = {
        enabled: true,
        value: discountValue,
      };
    }

    onSave(data);
  };

  const handleCancel = () => {
    // Reset form
    setSecurityDepositEnabled(false);
    setSecurityDepositValue('');
    setTaxProfileEnabled(false);
    setTaxProfileValue('');
    setDiscountEnabled(false);
    setDiscountValue('');
    onClose();
  };

  return (
    <BaseDialog
      isOpen={isOpen}
      onClose={handleCancel}
      title={`Edit ${customerCount} customers`}
      maxWidth="560px"
      showCloseButton={true}
      contentPadding="24px"
      actions={
        <>
          <Button appearance="secondary" onClick={handleCancel}>
            Cancel
          </Button>
          <Button appearance="primary" onClick={handleSave}>
            Edit all
          </Button>
        </>
      }
    >
      <div>
            <div className={styles.fieldRow}>
              <div className={styles.fieldLabel}>
                <Checkbox
                  checked={securityDepositEnabled}
                  onChange={(e, data) => setSecurityDepositEnabled(data.checked === true)}
                  label="Security deposit"
                />
              </div>
              <div className={styles.fieldInput}>
                <Dropdown
                  placeholder="Select..."
                  disabled={!securityDepositEnabled}
                  value={securityDepositValue}
                  onOptionSelect={(e, data) => setSecurityDepositValue(data.optionValue || '')}
                >
                  <Option value="none">None</Option>
                  <Option value="50">$50</Option>
                  <Option value="100">$100</Option>
                  <Option value="200">$200</Option>
                  <Option value="custom">Custom</Option>
                </Dropdown>
              </div>
            </div>

            <div className={styles.fieldRow}>
              <div className={styles.fieldLabel}>
                <Checkbox
                  checked={taxProfileEnabled}
                  onChange={(e, data) => setTaxProfileEnabled(data.checked === true)}
                  label="Tax profile"
                />
              </div>
              <div className={styles.fieldInput}>
                <Dropdown
                  placeholder="No tax profile"
                  disabled={!taxProfileEnabled}
                  value={taxProfileValue}
                  onOptionSelect={(e, data) => setTaxProfileValue(data.optionValue || '')}
                >
                  <Option value="none">No tax profile</Option>
                  <Option value="standard">Standard Tax</Option>
                  <Option value="exempt">Tax Exempt</Option>
                  <Option value="reduced">Reduced Rate</Option>
                </Dropdown>
              </div>
            </div>

            <div className={styles.fieldRow}>
              <div className={styles.fieldLabel}>
                <Checkbox
                  checked={discountEnabled}
                  onChange={(e, data) => setDiscountEnabled(data.checked === true)}
                  label="Discount percentage"
                />
              </div>
              <div className={styles.fieldInput}>
                <Input
                  placeholder="%"
                  disabled={!discountEnabled}
                  value={discountValue}
                  onChange={(e, data) => setDiscountValue(data.value)}
                  type="number"
                  min="0"
                  max="100"
                />
              </div>
            </div>
      </div>
    </BaseDialog>
  );
}
